import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';
import { IEmployeePlusPicture } from '../../interfaces/IAutoCompleteEmployeeDto';
import i18n, { getAppText } from 'src/i18n';
import { Mark } from '@material-ui/core';

export interface HierarchyDto {
    id: number,
    manager: IEmployeePlusPicture;
    directReports: IEmployeePlusPicture[];
    startDate: string;
    days: number;
    reason: string;
    creationDate: string;
    deleteDate: string | undefined;
}

export const HierarchyDurations: Mark[] = Array.from(
    { length: 30 },
    (_, index) => ({
        value: index + 1,
        label: `${index + 1}`,
    })
);

export const createBlankHierarchy = (): HierarchyDto => ({
    id: 0,
    manager: {
        id: 0,
        name: '',
        userName: '',
        picture: '/images/empty-profile.jpg',
    },
    directReports: [],
    startDate: new Date().toJSON(),
    days: 1,
    reason: '',
    creationDate: new Date().toJSON(),
    deleteDate: undefined,
});

export interface HierarchyState {
    hierarchies: HierarchyDto[];
    hierarchy: HierarchyDto;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    saving: boolean;
}

const initialState: HierarchyState = {
    hierarchies: [],
    hierarchy: createBlankHierarchy(),
    status: 'idle',
    saving: false
};

export const hierarchySlice = createSlice({
    name: 'hierarchy',
    initialState,
    reducers: {
        clearHierarchy: (state) => {
            state.hierarchy = createBlankHierarchy();
            state.saving = false;
            state.status = 'idle';
        },
        clearHierarchies: (state) => {
            state.hierarchies = [];
            state.saving = false;
            state.status = 'idle';
        },
        setManager: (state, action: PayloadAction<IEmployeePlusPicture>) => {
            state.hierarchy.manager = action.payload;
        },
        setDirectReport: (state, param: PayloadAction<IEmployeePlusPicture[]>) => {
            state.hierarchy.directReports = param.payload;
        },
        setStartDate: (state, param: PayloadAction<string>) => {
            state.hierarchy.startDate = param.payload;
        },
        setDays: (state, param: PayloadAction<number>) => {
            state.hierarchy.days = param.payload;
        },
        setReason: (state, param: PayloadAction<string>) => {
            state.hierarchy.reason = param.payload;
        },
        setCurrent: (state, param: PayloadAction<HierarchyDto>) => {
            state.hierarchy = param.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getHierarchies.pending, (state) => {
                state.saving = true;
                state.status = 'loading';
            })
            .addCase(getHierarchies.fulfilled, (state, action) => {
                state.saving = false;
                state.status = 'succeeded';
                state.hierarchies = action.payload ?? [];
            })
            .addCase(getHierarchies.rejected, (state) => {
                state.saving = false;
                state.status = 'failed';
                state.hierarchies = [];
                state.hierarchy = createBlankHierarchy();
            })
            .addCase(getHierarchy.pending, (state) => {
                state.saving = true;
                state.status = 'loading';
            })
            .addCase(getHierarchy.fulfilled, (state, action) => {
                state.saving = false;
                state.status = 'succeeded';
                state.hierarchy = action.payload;
            })
            .addCase(getHierarchy.rejected, (state) => {
                state.saving = false;
                state.status = 'failed';
                state.hierarchy = createBlankHierarchy();
            })
            .addCase(saveHierarchy.pending, (state) => {
                state.saving = true;
                state.status = 'loading';
            })
            .addCase(saveHierarchy.fulfilled, (state) => {
                state.saving = false;
                state.status = 'succeeded';
            })
            .addCase(saveHierarchy.rejected, (state) => {
                state.saving = false;
                state.status = 'failed';
            })
            .addCase(deleteHierarchy.pending, (state) => {
                state.saving = true;
                state.status = 'loading';
            })
            .addCase(deleteHierarchy.fulfilled, (state, action) => {
                state.saving = false;
                state.status = 'succeeded';
                state.hierarchies = state.hierarchies.filter(h => h.id !== action.meta.arg);
            })
            .addCase(deleteHierarchy.rejected, (state) => {
                state.saving = false;
                state.status = 'failed';
            });
    },
});

export const { clearHierarchies, clearHierarchy, setManager, setDirectReport, setStartDate, setDays, setReason, setCurrent } = hierarchySlice.actions;

export const getHierarchies = createAsyncThunk('hierarchy/getHierarchies', async () : Promise<HierarchyDto[]> => {
    const response = await axios.get('/api/hierarchy');

    return response.data;
});

export const getHierarchy = createAsyncThunk('hierarchy/getHierarchy', async (id: number) : Promise<HierarchyDto> => {
    const response = await axios.get(`/api/hierarchy/${id}`);

    return response.data;
});

export const deleteHierarchy = createAsyncThunk('hierarchy/deleteHierarchy', async (id: number, thunkAPI): Promise<number | unknown> => {
    try {
        await axios.delete(`/api/hierarchy/${id}`);

        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('archivedHierarchy')
            })
        );

        return id;
    } catch(error: any) {
        const message = error?.response?.data?.errors?.map((msg: any) => msg.summary)?.join(', ')
                        ?? error?.response?.data
                        ?? error?.message
                        ?? 'Unexpected error';

        thunkAPI.dispatch(showMessage({
            type: 'error',
            message: i18n.t(message),
        }));

        return thunkAPI.rejectWithValue(message);
    }
});

export const saveHierarchy = createAsyncThunk('hierarchy/saveHierarchy', async (hierarchyToSave: HierarchyDto, thunkAPI): Promise<HierarchyDto | unknown> => {
    try {

        let message = getAppText('createHierarchy');

        if (hierarchyToSave.id === 0) {
            await axios.post('/api/hierarchy', hierarchyToSave);
        } else {
            await axios.put(`/api/hierarchy/${hierarchyToSave.id}`, hierarchyToSave);
            message = getAppText('saveHierarchy');
        }

        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: message
            })
        );
        
        return createBlankHierarchy();

    } catch(error: any) {
        const message = error?.response?.data?.errors?.map((msg: any) => msg.summary)?.join(', ')
                        ?? error?.response?.data
                        ?? error?.message
                        ?? 'Unexpected error';

        thunkAPI.dispatch(showMessage({
            type: 'error',
            message: i18n.t(message),
        }));

        return thunkAPI.rejectWithValue(message);
    }
});
